import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LogOut, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import vcmLogo from "@/assets/vcm-logo-small.webp";
import NotificationBell from "./NotificationBell";
import { InstallButton } from "./pwa/InstallButton";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [mobileNotifCount, setMobileNotifCount] = useState(0);
  const [pendingCallRequests, setPendingCallRequests] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsLoggedIn(!!session);
      setUserId(session?.user?.id || null);
      
      if (session?.user) {
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .eq('role', 'admin')
          .single();
        
        setIsAdmin(!!roles);
      } else {
        setIsAdmin(false);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
      setUserId(session?.user?.id || null);
      
      if (session?.user) {
        setTimeout(async () => {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .eq('role', 'admin')
            .single();
          
          setIsAdmin(!!roles);
        }, 0);
      } else {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Track notification count for mobile badge
  useEffect(() => {
    if (!userId) {
      setMobileNotifCount(0);
      return;
    }

    const loadNotifCount = async () => {
      try {
        const { count: msgCount } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('recipient_id', userId)
          .eq('is_read', false);

        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const { data: matchData } = await supabase
          .from('battle_matches')
          .select(`
            id,
            updated_at,
            request_1:battle_requests!request_1_id(creator_id),
            request_2:battle_requests!request_2_id(creator_id)
          `)
          .gte('updated_at', sevenDaysAgo.toISOString());

        const matchCount = (matchData || []).filter((match: any) => {
          const creator1Id = match.request_1?.creator_id;
          const creator2Id = match.request_2?.creator_id;
          return creator1Id === userId || creator2Id === userId;
        }).length;

        setMobileNotifCount((msgCount || 0) + matchCount);
      } catch (error) {
        console.error('Error loading notification count:', error);
      }
    };

    loadNotifCount();

    const channel = supabase
      .channel('mobile-notif-count')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, loadNotifCount)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'battle_matches' }, loadNotifCount)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // Track pending call requests for admin badge
  useEffect(() => {
    if (!isAdmin) {
      setPendingCallRequests(0);
      return;
    }

    const loadPendingCallRequests = async () => {
      try {
        const { count } = await supabase
          .from('call_requests')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending');
        
        setPendingCallRequests(count || 0);
      } catch (error) {
        console.error('Error loading pending call requests:', error);
      }
    };

    loadPendingCallRequests();

    const channel = supabase
      .channel('admin-call-requests')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'call_requests' }, loadPendingCallRequests)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Signed out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Failed to sign out");
    }
  };

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Why VCM", path: "/why" },
    { name: "Success Stories", path: "/success" },
    { name: "The Program", path: "/program" },
    { name: "360° Management", path: "/talent-management" },
    { name: "Battle Request", path: "/battle-request" },
    { name: "Battle Stats", path: "/stats" },
    { name: "Creator Portal", path: "/creator-portal" },
    { name: "FAQ", path: "/faq" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 pt-[env(safe-area-inset-top)] ${
          scrolled 
            ? 'bg-background/95 backdrop-blur-xl border-b border-border shadow-lg shadow-primary/5' 
            : 'bg-background/80 backdrop-blur-xl border-b border-border/50'
        }`}
      >
        {/* Top gradient accent line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-primary via-secondary to-accent opacity-80" />
        <div className="container mx-auto px-4 py-3 md:py-4 pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))]">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 group">
              <img 
                src={vcmLogo} 
                alt="Viral Check Media" 
                width="140" 
                height="98"
                fetchPriority="high"
                className="h-9 md:h-12 w-auto brightness-110 contrast-125 transition-transform duration-300 group-hover:scale-105" 
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-all duration-200 hover:text-primary relative group ${
                    location.pathname === link.path ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {link.name}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-primary transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </Link>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <InstallButton variant="icon" />
              {isLoggedIn ? (
                <>
                  {userId && <NotificationBell userId={userId} />}
                  {isAdmin && (
                    <Link to="/admin" className="relative">
                      <Button variant="outline" size="default" className="hover-lift">
                        Admin Dashboard
                      </Button>
                      {pendingCallRequests > 0 && (
                        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium animate-pulse">
                          {pendingCallRequests > 9 ? '9+' : pendingCallRequests}
                        </span>
                      )}
                    </Link>
                  )}
                  <Button variant="ghost" size="default" onClick={handleLogout} className="group">
                    <LogOut className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                    Sign Out
                  </Button>
                </>
              ) : (
                <Link to="/apply">
                  <Button variant="premium" size="default" className="hover-lift">
                    Apply Now
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-foreground relative p-2 -mr-2 tap-target focus-ring rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
            >
              <div className="relative w-6 h-6">
                <Menu 
                  size={24} 
                  className={`absolute inset-0 transition-all duration-300 ${mobileMenuOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`} 
                />
                <X 
                  size={24} 
                  className={`absolute inset-0 transition-all duration-300 ${mobileMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`} 
                />
              </div>
              {!mobileMenuOpen && isLoggedIn && mobileNotifCount > 0 && (
                <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-medium animate-pulse">
                  {mobileNotifCount > 9 ? '9+' : mobileNotifCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-[85%] max-w-sm bg-background border-l border-border z-50 lg:hidden transition-transform duration-300 ease-out pt-[calc(env(safe-area-inset-top)+4rem)] pb-[env(safe-area-inset-bottom)] ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto px-6 py-4">
          {/* Navigation Links */}
          <nav className="flex-1 space-y-1">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center justify-between py-3 px-4 rounded-lg text-base font-medium transition-all duration-200 tap-target ${
                  location.pathname === link.path 
                    ? "bg-primary/10 text-primary" 
                    : "text-foreground hover:bg-muted"
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span>{link.name}</span>
                <ChevronRight className={`w-4 h-4 transition-transform ${
                  location.pathname === link.path ? 'text-primary' : 'text-muted-foreground'
                }`} />
              </Link>
            ))}
          </nav>

          {/* Bottom Actions */}
          <div className="mt-6 pt-6 border-t border-border space-y-3">
            {isLoggedIn ? (
              <>
                {userId && (
                  <div className="flex items-center justify-between py-2 px-4">
                    <span className="text-sm text-muted-foreground">Notifications</span>
                    <NotificationBell userId={userId} />
                  </div>
                )}
                <div className="flex items-center justify-between py-2 px-4">
                  <span className="text-sm text-muted-foreground">Install App</span>
                  <InstallButton variant="icon" />
                </div>
                {isAdmin && (
                  <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="block">
                    <Button variant="outline" size="lg" className="w-full justify-between">
                      Admin Dashboard
                      {pendingCallRequests > 0 && (
                        <span className="h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
                          {pendingCallRequests > 9 ? '9+' : pendingCallRequests}
                        </span>
                      )}
                    </Button>
                  </Link>
                )}
                <Button 
                  variant="ghost" 
                  size="lg" 
                  className="w-full justify-start"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between py-2 px-4">
                  <span className="text-sm text-muted-foreground">Install App</span>
                  <InstallButton variant="icon" />
                </div>
                <Link to="/apply" onClick={() => setMobileMenuOpen(false)} className="block">
                  <Button variant="premium" size="lg" className="w-full">
                    Apply Now
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
